import React, { useState, useRef } from 'react'
import LoadingButton from '../shared/LoadingButton'
import LoginFrame, { LoginFrameChildrenProps } from '../shared/LoginFrame'
import { contextSignupSubmit, eventHandler } from '../../types.d'
import { preventDefault, preventDefaultAndValidate } from '../../helpers'
import {
  InjectedFrameProps,
  withFrameProps,
} from '../../context/withFrameProps'
import { useSignupSubmit, useAppDispatch } from '../../hooks/useAuthActions'
import { useAppSelector, selectSignupData } from '../../store/store'
import { editSignupEmail, editSignupName } from '../../store'

type SigninConfirmContentsProps = {
  displayName: string
  email: string
  onEditEmail: eventHandler
  onEditName: eventHandler
  onSubmit: contextSignupSubmit
} & LoginFrameChildrenProps

const PureSignupConfirmContents = ({
  recaptchaPromise,
  recaptchaLoading,
  displayName,
  email,
  onSubmit,
  onEditEmail,
  onEditName,
}: SigninConfirmContentsProps) => {
  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    onSubmit() // TODO passkey
  }

  return (
    <>
      <div>
        <label
          htmlFor="email"
          className="block mb-2 text-sm font-medium text-gray-900"
        >
          Your email
        </label>
        <div className="relative mb-6">
          <button
            id="edit-email"
            aria-label="Edit email"
            onClick={preventDefault(onEditEmail)}
            tabIndex={1}
            className="absolute inset-y-0 end-0 flex items-center p-2 text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z"
              />
            </svg>
          </button>
          <input
            disabled
            value={email}
            id="email"
            type="text"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 pe-10"
          />
        </div>
      </div>
      <div>
        <label
          htmlFor="name"
          className="block mb-2 text-sm font-medium text-gray-900"
        >
          Your name
        </label>
        <div className="relative mb-6">
          <button
            id="edit-name"
            aria-label="Edit name"
            onClick={preventDefault(onEditName)}
            tabIndex={2}
            className="absolute inset-y-0 end-0 flex items-center p-2 text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z"
              />
            </svg>
          </button>
          <input
            disabled
            value={displayName}
            id="name"
            type="text"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 pe-10"
          />
        </div>
      </div>
      <div className="flex items-center">
        <input
          tabIndex={3}
          id="terms_and_conditions"
          type="checkbox"
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500"
          required
        />
        <label
          htmlFor="terms_and_conditions"
          className="ms-3 text-sm font-medium text-gray-900"
        >
          By submitting this information you agree to our{' '}
          <a
            href="https://tidyhq.com/terms-of-service/"
            className="text-blue-600 hover:underline"
          >
            Terms&nbsp;and&nbsp;Conditions
          </a>
          .
        </label>
      </div>
      <LoadingButton
        onClick={preventDefaultAndValidate(handleSubmit)}
        loading={recaptchaLoading}
        tabIndex={4}
        type="submit"
        className="w-full text-white bg-slate-600 hover:bg-slate-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
      >
        Create Account
      </LoadingButton>
    </>
  )
}

type PureSignupConfirmProps = {
  displayName: string
  email: string
  onSubmit: contextSignupSubmit
  onEditEmail: eventHandler
  onEditName: eventHandler
  recaptcha?: boolean
} & InjectedFrameProps

export const PureSignupConfirm = ({
  loading,
  error,
  onPreviousStep,
  recaptchaSiteKey,
  logoUrl = null,
  ...props
}: PureSignupConfirmProps) => (
  <LoginFrame
    title={'Confirm your details'}
    recaptchaSiteKey={null}
    {...{ loading, error, onPreviousStep, logoUrl }}
  >
    <PureSignupConfirmContents {...props} />
  </LoginFrame>
)

const BoundSignupConfirm = withFrameProps(PureSignupConfirm, {
  hasPreviousStep: true,
})

export const SignupConfirm = () => {
  const dispatch = useAppDispatch()
  const signupSubmit = useSignupSubmit()
  const signupData = useAppSelector(selectSignupData)

  if (!signupData) {
    return null // This shouldn't happen but provides type safety
  }

  const { email, firstName, lastName } = signupData

  const onEditEmail = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    dispatch(editSignupEmail())
  }

  const onEditName = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    dispatch(editSignupName())
  }

  return (
    <BoundSignupConfirm
      onSubmit={signupSubmit}
      displayName={`${firstName} ${lastName}`}
      {...{ email, onEditEmail, onEditName }}
    />
  )
}

export default SignupConfirm
